<template>
    <div
        v-if="!isLoading && items.length"
        class="mt-4"
    >
        <BaseCollapse v-model="activeCollapse">
            <BaseCollapseItem
                :name="'selected-items'"
                :title="$t('autocomplete.defaultOrder')"
            >
                <div class="selected-items-list">
                    <draggable
                        :list="itemsRef"
                        handle=".drag-handle"
                        class="space-y-2"
                        @start="isDragging = true"
                        @end="onDragEnd"
                    >
                        <div
                            v-for="item in itemsRef"
                            :key="item.key"
                            class="drag-handle selected-order-item flex items-center gap-2 text-sm py-1 cursor-grab rounded px-1"
                        >
                            <RedesignBaseIcon
                                name="GripVertical"
                                class="w-4 h-4 flex-shrink-0"
                            />
                            <RedesignBaseIcon
                                v-if="entityIcon"
                                :name="entityIcon"
                                class="w-4 h-4 flex-shrink-0"
                            />
                            <span>{{ item.label }}</span>
                        </div>
                    </draggable>
                </div>
            </BaseCollapseItem>
        </BaseCollapse>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { ENTITY_SELECTION_TYPE, type EntitySelectionType } from '@/enum/entitySelection'
import draggable from 'vuedraggable'

/* Types */
type DisplayItem = {
    key: string | number
    label: string
}

/* Props */
const props = defineProps<{
    items: DisplayItem[]
    selectionType: EntitySelectionType
    entityIcon?: string
    isLoading: boolean
}>()

const emit = defineEmits<{
    (e: 'update:order', orderedKeys: (string | number)[]): void
}>()

/* Data */
const activeCollapse = ref<string[]>([])
const itemsRef = ref<DisplayItem[]>([])
const isDragging = ref(false)

function onDragEnd () {
    isDragging.value = false

    const orderedKeys = itemsRef.value.map(item => item.key)

    emit('update:order', orderedKeys)
}

watch(
    () => props.items,
    (newItems) => {
        if (!isDragging.value) {
            itemsRef.value = [ ...newItems ]
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.selected-items-list {
    max-height: 400px;
    overflow-y: auto;

    .selected-order-item {
        color: var(--mainTextColor-color);

        &:hover {
            background-color: color-mix(in srgb, var(--mainTextColor-color) 6%, transparent);
            color: var(--mainTextColor-color);
        }
    }

    .sortable-chosen,
    .sortable-ghost,
    .sortable-drag {
        background-color: color-mix(in srgb, var(--mainTextColor-color) 10%, transparent);
        color: var(--mainTextColor-color);
    }
}
</style>
